import { FFmpeg, FFFSType } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';

type ConvertProgressCallback = (progress: number) => void;

const FFMPEG_CORE_VERSION = '0.12.9';
const FFMPEG_CORE_BASE_URL = `https://unpkg.com/@ffmpeg/core@${FFMPEG_CORE_VERSION}/dist/umd`;

const MIN_CONVERSION_TIMEOUT_MS = 10 * 60 * 1000;
const MAX_CONVERSION_TIMEOUT_MS = 60 * 60 * 1000;
const CONVERSION_BUDGET_PER_VIDEO_SECOND_MS = 4000;
const DEFAULT_DURATION_SECONDS = 600;
const METADATA_PROBE_TIMEOUT_MS = 15 * 1000;
const MAX_OUTPUT_WIDTH = 1280;

export type VideoConverterErrorCode =
  | 'UNSUPPORTED_FORMAT'
  | 'LOAD_FAILED'
  | 'OUT_OF_MEMORY'
  | 'CONVERSION_FAILED';

export class VideoConverterError extends Error {
  code: VideoConverterErrorCode;

  constructor(code: VideoConverterErrorCode, message: string) {
    super(message);
    this.name = 'VideoConverterError';
    this.code = code;
    Object.setPrototypeOf(this, VideoConverterError.prototype);
  }
}

const getBaseFilename = (filename: string) => {
  const normalized = filename.trim() || 'spot-video';
  return normalized.replace(/\.[^/.]+$/, '') || 'spot-video';
};

const getExtension = (filename: string) => {
  const match = filename.trim().match(/\.([^.]+)$/);
  return match?.[1]?.toLowerCase() || '';
};

export const buildWebmFilename = (originalName: string): string => {
  const base = getBaseFilename(originalName)
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^[-.]+|[-.]+$/g, '');
  return `${base || 'spot-video'}.webm`;
};

export type VideoFileValidation =
  | { ok: false; code: VideoConverterErrorCode; message: string }
  | { ok: true; isWebm: boolean; needsConversion: boolean };

export const parseAndValidateVideoFile = (file: File): VideoFileValidation => {
  const extension = getExtension(file.name);
  const isWebm = file.type === 'video/webm' || extension === 'webm';
  const needsConversion =
    file.type === 'video/mp4' ||
    file.type === 'video/quicktime' ||
    ['mp4', 'mov'].includes(extension);

  if (!isWebm && !needsConversion) {
    return {
      ok: false,
      code: 'UNSUPPORTED_FORMAT',
      message: 'Please upload a WebM video, or an MP4/MOV file to convert.',
    };
  }

  return { ok: true, isWebm, needsConversion };
};

type VideoMetadata = {
  duration: number | null;
  width: number | null;
  height: number | null;
};

export const probeVideoMetadata = (file: File): Promise<VideoMetadata> =>
  new Promise((resolve) => {
    const empty: VideoMetadata = { duration: null, width: null, height: null };

    if (
      typeof document === 'undefined' ||
      typeof URL === 'undefined' ||
      typeof URL.createObjectURL !== 'function'
    ) {
      resolve(empty);
      return;
    }

    const video = document.createElement('video');
    const objectUrl = URL.createObjectURL(file);
    let settled = false;

    const finish = (metadata: VideoMetadata) => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timer);
      video.onloadedmetadata = null;
      video.onerror = null;
      video.removeAttribute('src');
      URL.revokeObjectURL(objectUrl);
      resolve(metadata);
    };

    const timer = setTimeout(() => finish(empty), METADATA_PROBE_TIMEOUT_MS);

    video.preload = 'metadata';
    video.muted = true;
    video.onloadedmetadata = () => {
      finish({
        duration: Number.isFinite(video.duration) ? video.duration : null,
        width: video.videoWidth || null,
        height: video.videoHeight || null,
      });
    };
    video.onerror = () => finish(empty);
    video.src = objectUrl;
  });

let ffmpeg: FFmpeg | null = null;
let ffmpegLoadPromise: Promise<void> | null = null;

const resetFfmpeg = () => {
  if (ffmpeg) {
    try {
      ffmpeg.terminate();
    } catch {
      ffmpeg = null;
    }
  }
  ffmpeg = null;
  ffmpegLoadPromise = null;
};

const getFfmpeg = async (): Promise<FFmpeg> => {
  if (!ffmpeg) {
    ffmpeg = new FFmpeg();
  }
  const instance = ffmpeg;

  if (instance.loaded) {
    return instance;
  }

  if (!ffmpegLoadPromise) {
    ffmpegLoadPromise = (async () => {
      const [coreURL, wasmURL] = await Promise.all([
        toBlobURL(`${FFMPEG_CORE_BASE_URL}/ffmpeg-core.js`, 'text/javascript'),
        toBlobURL(`${FFMPEG_CORE_BASE_URL}/ffmpeg-core.wasm`, 'application/wasm'),
      ]);
      await instance.load({ coreURL, wasmURL });
    })();
  }

  try {
    await ffmpegLoadPromise;
  } catch {
    resetFfmpeg();
    throw new VideoConverterError(
      'LOAD_FAILED',
      'Could not load the video converter. Check your connection and try again.',
    );
  }

  return instance;
};

const OOM_SIGNATURES = [
  'out of memory',
  'memory access out of bounds',
  'cannot enlarge memory',
  'table index is out of bounds',
  'abort(oom',
  'aborted',
];

const looksLikeOom = (text: string): boolean => {
  const lower = text.toLowerCase();
  return OOM_SIGNATURES.some((signature) => lower.includes(signature));
};

const OUT_OF_MEMORY_MESSAGE =
  'This video is too large to convert in the browser — it ran out of memory. Please try a shorter or lower-resolution clip, compress it, or upload a WebM file.';
const CONVERSION_FAILED_MESSAGE =
  'Video conversion failed or timed out. Please try again, or upload a WebM file.';

const resolveExecTimeout = (durationSeconds: number | null): number => {
  const budget =
    (durationSeconds ?? DEFAULT_DURATION_SECONDS) *
    CONVERSION_BUDGET_PER_VIDEO_SECOND_MS;
  return Math.min(
    MAX_CONVERSION_TIMEOUT_MS,
    Math.max(MIN_CONVERSION_TIMEOUT_MS, Math.round(budget)),
  );
};

const buildScaleArgs = (
  width: number | null,
  height: number | null,
): string[] => {
  if (!width || !height || width <= MAX_OUTPUT_WIDTH) {
    return [];
  }
  const targetWidth = MAX_OUTPUT_WIDTH;
  const scaledHeight = Math.round((height * MAX_OUTPUT_WIDTH) / width);
  const targetHeight = Math.max(2, scaledHeight - (scaledHeight % 2));
  return ['-vf', `scale=${targetWidth}:${targetHeight}`];
};

export const convertVideoFileToWebm = async (
  file: File,
  onProgress?: ConvertProgressCallback,
): Promise<File> => {
  const validation = parseAndValidateVideoFile(file);
  if (!validation.ok) {
    throw new VideoConverterError(validation.code, validation.message);
  }

  const outputFilename = buildWebmFilename(file.name);

  if (validation.isWebm && !validation.needsConversion) {
    if (file.name === outputFilename && file.type === 'video/webm') {
      return file;
    }
    return new File([file], outputFilename, { type: 'video/webm' });
  }

  const { duration, width, height } = await probeVideoMetadata(file);
  const execTimeoutMs = resolveExecTimeout(duration);
  const scaleArgs = buildScaleArgs(width, height);

  const instance = await getFfmpeg();

  const inputExtension = getExtension(file.name) || 'mp4';
  const stamp = Date.now();
  const mountDir = `/mount-${stamp}`;
  const inputName = `source.${inputExtension}`;
  const outputPath = `output-${stamp}.webm`;

  const logTail: string[] = [];
  const handleLog = ({ message }: { message: string }) => {
    logTail.push(message);
    if (logTail.length > 60) {
      logTail.shift();
    }
  };
  const handleProgress = ({ progress }: { progress: number }) => {
    if (onProgress) {
      onProgress(Math.round(Math.min(Math.max(progress, 0), 1) * 100));
    }
  };

  instance.on('log', handleLog);
  instance.on('progress', handleProgress);

  let crashed = false;
  let usedWorkerFs = false;
  let inputPath = `mem-${inputName}`;

  const logFailure = (label: string, exitCode?: number) => {
    console.error(`Video conversion ${label}`, {
      fileName: file.name,
      exitCode,
      usedWorkerFs,
      ffmpegLog: logTail.slice(-12).join(' | '),
    });
  };

  try {
    try {
      await instance.createDir(mountDir);
      const mountedOk = await instance.mount(
        FFFSType.WORKERFS,
        { blobs: [{ name: inputName, data: file }] },
        mountDir,
      );
      if (!mountedOk) {
        throw new Error('WORKERFS mount returned false');
      }
      usedWorkerFs = true;
      inputPath = `${mountDir}/${inputName}`;
    } catch (mountError) {
      console.warn('WORKERFS unavailable, falling back to in-memory input', {
        fileName: file.name,
        error: (mountError as Error)?.message,
      });
      await instance.deleteDir(mountDir).catch(() => undefined);
      inputPath = `mem-${inputName}`;
      await instance.writeFile(inputPath, await fetchFile(file));
    }

    const exitCode = await instance.exec(
      [
        '-i',
        inputPath,
        ...scaleArgs,
        '-c:v',
        'libvpx',
        '-b:v',
        '1M',
        '-deadline',
        'good',
        '-cpu-used',
        '5',
        '-pix_fmt',
        'yuv420p',
        '-c:a',
        'libopus',
        '-b:a',
        '96k',
        '-f',
        'webm',
        outputPath,
      ],
      execTimeoutMs,
    );

    if (exitCode !== 0) {
      crashed = true;
      const isOom = looksLikeOom(logTail.join('\n'));
      logFailure(isOom ? 'ran out of memory' : 'failed', exitCode);
      throw new VideoConverterError(
        isOom ? 'OUT_OF_MEMORY' : 'CONVERSION_FAILED',
        isOom ? OUT_OF_MEMORY_MESSAGE : CONVERSION_FAILED_MESSAGE,
      );
    }

    const outputData = await instance.readFile(outputPath);
    const bytes =
      typeof outputData === 'string'
        ? new TextEncoder().encode(outputData)
        : outputData;

    if (!bytes || bytes.length === 0) {
      crashed = true;
      logFailure('produced an empty file', exitCode);
      throw new VideoConverterError(
        'CONVERSION_FAILED',
        CONVERSION_FAILED_MESSAGE,
      );
    }

    return new File([bytes], outputFilename, { type: 'video/webm' });
  } catch (conversionError) {
    crashed = true;

    if (conversionError instanceof VideoConverterError) {
      throw conversionError;
    }

    const rawMessage = (conversionError as Error)?.message ?? '';
    const isOom = looksLikeOom(rawMessage) || looksLikeOom(logTail.join('\n'));
    logFailure(`threw: ${rawMessage}`);
    throw new VideoConverterError(
      isOom ? 'OUT_OF_MEMORY' : 'CONVERSION_FAILED',
      isOom ? OUT_OF_MEMORY_MESSAGE : CONVERSION_FAILED_MESSAGE,
    );
  } finally {
    instance.off('log', handleLog);
    instance.off('progress', handleProgress);

    if (crashed) {
      resetFfmpeg();
    } else {
      if (usedWorkerFs) {
        await instance.unmount(mountDir).catch(() => undefined);
        await instance.deleteDir(mountDir).catch(() => undefined);
      } else {
        await instance.deleteFile(inputPath).catch(() => undefined);
      }
      await instance.deleteFile(outputPath).catch(() => undefined);
    }
  }
};
